'use client'

import React, { memo, useEffect, useRef, useState } from 'react'

import { useVirtualizer } from '@tanstack/react-virtual'
import {
  HiChevronDown,
  HiChevronUp,
  HiOutlineArrowDownTray,
  HiOutlineBellAlert,
  HiOutlineBolt,
  HiOutlinePlusCircle,
} from 'react-icons/hi2'
import remarkGfm from 'remark-gfm'
import { twMerge } from 'tailwind-merge'

import DemoTooltip from '@/components/billing/DemoTooltip'
import Button from '@/components/common/Button'
import IconButton from '@/components/common/IconButton'
import Link from '@/components/common/Link'
import { OrgPermissionGate } from '@/components/common/PermissionGate'
import Spinner from '@/components/common/Spinner'
import Code from '@/components/markdown/Code'
import Markdown from '@/components/markdown/Markdown'
import Pre from '@/components/markdown/Pre'
import Table from '@/components/markdown/Table'
import Td from '@/components/markdown/Td'
import Th from '@/components/markdown/Th'
import Thead from '@/components/markdown/Thead'
import { useSearchContext } from '@/components/search/context'
import Opportunity from '@/components/search/Opportunity'
import SearchStep, { LoadingStep } from '@/components/search/SearchStep'
import SledSourceRequestBanner from '@/components/search/SledSourceRequestBanner'

import { SLED_PORTAL_REQUEST_URL } from '@/constants/env'
import { PROSE_CLASSNAMES } from '@/constants/styles'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'
import { useCanAccessBasic } from '@/hooks/billing/useCanAccessBasic'
import { useMounted } from '@/hooks/common/useMounted'

const DEFAULT_SEARCH_RESULT_COUNT: number = 50

interface SearchResponseDisplay {
  requiredTagNames: string[]
}

interface SearchSteps {
  steps: string[]
}

const SearchSteps: React.FC<SearchSteps> = ({ steps }) => {
  if (steps.length === 0) {
    return (
      <div className="flex flex-wrap items-center w-full gap-2">
        {Array.from({ length: 3 }).map((_, idx) => (
          <LoadingStep key={idx} />
        ))}
      </div>
    )
  }
  return (
    <>
      <div className="hidden md:flex flex-wrap items-start w-full gap-y-2 gap-x-1">
        {steps.map((step, idx) => (
          <SearchStep
            key={idx}
            value={step}
            completed={steps.length === 1 ? false : idx < steps.length - 1}
          />
        ))}
      </div>
      <div className="md:hidden flex flex-wrap items-start w-full">
        {steps.length > 0 && (
          <SearchStep value={steps[steps.length - 1]} completed={false} />
        )}
      </div>
    </>
  )
}

const BANNER_INDEX_FEW_RESULTS = 1
const BANNER_INDEX_MANY_RESULTS = 6
const BANNER_MANY_RESULTS_THRESHOLD = 10

const SearchResponseDisplay: React.FC<SearchResponseDisplay> = ({
  requiredTagNames,
}) => {
  const {
    data,
    dataIsLoading,
    exportIsPending,
    isStreaming,
    query,
    steps,
    summary: _summary,
    summaryIsLoading,
    type,
    handleCreateSchedule,
    handleExport,
    handleInfiniteScroll,
  } = useSearchContext()

  const { data: billingOrganization } = useBillingOrganization()
  const { canAccessBasic } = useCanAccessBasic()

  const isMounted = useMounted()

  const canExport =
    billingOrganization !== undefined &&
    billingOrganization !== null &&
    billingOrganization.tier !== 'free'

  const [isClamped, setIsClamped] = useState<boolean>(false)
  const [summaryIsOpen, setSummaryIsOpen] = useState<boolean>(false)

  const infiniteScrollTriggerRef = useRef<HTMLDivElement>(null)
  const markdownRef = useRef<HTMLDivElement>(null)
  const parentRef = useRef<HTMLDivElement>(null)
  const topDivRef = useRef<HTMLDivElement>(null)
  const scrollToTopDivRef = useRef<HTMLDivElement>(null)

  const shouldShowBanner =
    type === 'sled_contract' && query.trim() !== '' && data

  const bannerIndex =
    data && data.data.length < BANNER_MANY_RESULTS_THRESHOLD
      ? BANNER_INDEX_FEW_RESULTS
      : BANNER_INDEX_MANY_RESULTS

  type DisplayItem =
    | { kind: 'banner' }
    | { kind: 'opportunity'; opportunity: any }

  const displayItems: DisplayItem[] = React.useMemo(() => {
    const items: DisplayItem[] = (data?.data ?? []).map((opportunity) => ({
      kind: 'opportunity',
      opportunity,
    }))
    if (shouldShowBanner) {
      const insertAt = Math.max(0, (bannerIndex ?? 1) - 1)
      items.splice(insertAt, 0, { kind: 'banner' })
    }
    return items
  }, [data?.data, shouldShowBanner, bannerIndex])

  const virtualizer = useVirtualizer({
    // 1 for the summary container at index 0, rest for display items
    count: 1 + (displayItems.length || 0),
    getScrollElement: () => parentRef.current,
    estimateSize: () => (dataIsLoading || data ? 210 : 0),
    overscan: 2,
    getItemKey: (index) => {
      if (index === 0) return 'summary'
      const item = displayItems[index - 1]
      if (item?.kind === 'banner') return 'banner'
      // prefer stable id if present to minimize re-measurement
      const id = (item as any)?.opportunity?.id
      return id ? `op-${id}` : `op-idx-${index - 1}`
    },
  })

  const summary = _summary
    .replace('Sweetspot Search Recommendations', '')
    // remove backticks since ai occasionally hallucinates
    .replaceAll('`', '')
    .replaceAll('$', '')
    .trim()

  const virtualizedItems = virtualizer.getVirtualItems()

  const handleOpenSummary = () => {
    // disable auto scroll when we expand the summary to prevent it from
    // shifting the entire page
    virtualizer.shouldAdjustScrollPositionOnItemSizeChange = () => false
    setSummaryIsOpen(!summaryIsOpen)
    // re-enable auto scroll when we collapse the summary
    virtualizer.shouldAdjustScrollPositionOnItemSizeChange = undefined
  }

  useEffect(() => {
    if (markdownRef.current) {
      const divHeight = markdownRef.current.offsetHeight
      const lineHeight = 20 // tailwind text-sm
      const lines = Math.floor(divHeight / lineHeight)
      setIsClamped(lines > 10)
    }
  }, [summary])

  useEffect(() => {
    if (summaryIsLoading) {
      scrollToTopDivRef.current?.scrollIntoView({ behavior: 'instant' })
    }
  }, [summaryIsLoading])

  useEffect(() => {
    if (summaryIsOpen) {
      parentRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [summaryIsOpen])

  useEffect(() => {
    const el = infiniteScrollTriggerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (
            typeof handleInfiniteScroll === 'function' &&
            data?.data &&
            data.has_more
          ) {
            handleInfiniteScroll(data.data.length)
          }
        }
      },
      {
        root: null,
        rootMargin: '50px',
        threshold: 0.5, // trigger when 50% of the element is visible
      },
    )

    if (infiniteScrollTriggerRef.current) {
      observer.observe(infiniteScrollTriggerRef.current)
    }

    return () => {
      if (infiniteScrollTriggerRef.current) {
        observer.unobserve(infiniteScrollTriggerRef.current)
      }
    }
  }, [data, infiniteScrollTriggerRef.current, handleInfiniteScroll])

  if (!isMounted) {
    return
  }

  return (
    <>
      <div
        ref={parentRef}
        className="relative w-full h-full overflow-y-auto overflow-x-hidden bg-primary-50 dark:bg-default-dark"
        style={{ contain: 'strict' }}
      >
        <div ref={scrollToTopDivRef} className="h-0" />
        <div
          className="w-full"
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {/*
          unfortunately, this virtualized item loop conditional logic was the only way i could
          get scrolling to not be glitchy while still having everything be within the
          the virtualized area (this was needed in order to make every component scrollable like on Google)
        */}
          {virtualizedItems.map((virtualizedItem) => {
            if (virtualizedItem.index === 0) {
              return (
                <div
                  className="flex flex-col items-center w-full"
                  data-index={virtualizedItems[0]?.index ?? 1}
                  key={virtualizedItems[0]?.index ?? 1}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transform: `translateY(${
                      virtualizedItems[0]?.start ?? 0
                    }px)`,
                  }}
                >
                  <div
                    ref={topDivRef}
                    className="flex items-center justify-between w-full pt-4 pb-4 px-4 5xl:px-0 max-w-screen-3xl"
                  >
                    <span className="text-md font-bold whitespace-nowrap text-primary-900 dark:text-primary-100">
                      {data !== undefined
                        ? `${data.count < DEFAULT_SEARCH_RESULT_COUNT ? data.count : `${DEFAULT_SEARCH_RESULT_COUNT}+`} result${
                            data.count === 1 ? '' : 's'
                          }`
                        : dataIsLoading
                          ? 'Loading...'
                          : ''}
                    </span>
                    {/* h-7 to prevent layout shift */}
                    <div className="flex items-center justify-center gap-x-2 h-7">
                      {data !== undefined && (
                        <>
                          <DemoTooltip childrenOnly={canExport}>
                            <Button
                              variant="outline-compact"
                              className="hidden md:flex bg-white dark:bg-primary-900"
                              onClick={handleExport}
                              isLoading={exportIsPending}
                              disabled={!canExport}
                            >
                              <HiOutlineArrowDownTray className="h-[18px] w-[18px] text-primary-700 dark:text-primary-300" />
                              <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                                Export
                              </span>
                            </Button>
                          </DemoTooltip>
                          {type === 'sled_contract' && (
                            <Link
                              href={SLED_PORTAL_REQUEST_URL}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Button
                                variant="outline-compact"
                                className="bg-white dark:bg-primary-900"
                              >
                                <HiOutlinePlusCircle className="h-[18px] w-[18px] text-primary-700 dark:text-primary-300" />
                                <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                                  Request Portals
                                </span>
                              </Button>
                            </Link>
                          )}
                          <OrgPermissionGate
                            permission="write"
                            fallback="hide"
                            asChild
                          >
                            <Button
                              variant="primary-compact"
                              onClick={handleCreateSchedule}
                            >
                              <HiOutlineBellAlert className="h-[18px] w-[18px]" />
                              <span className="text-sm font-medium">
                                Notify Me Daily
                              </span>
                            </Button>
                          </OrgPermissionGate>
                        </>
                      )}
                    </div>
                  </div>
                  <div
                    className={twMerge(
                      // TODO: this is a mess
                      'relative 5xl:border-x flex justify-center w-full bg-white dark:bg-accent-800/10 max-w-screen-3xl 5xl:rounded-md',
                      // if summary is loading (e.g. not yet streaming), show default border, otherwise show primary border
                      summaryIsLoading
                        ? 'border-b md:border-b border-primary-200 dark:border-primary-800'
                        : 'border-accent-400 dark:border-accent-900',
                      // if summary is not empty and the summary isn't streaming and the summary is open, show border-b, otherwise show border-b-0
                      summaryIsLoading || summary !== ''
                        ? twMerge(
                            'lg:min-h-[156px] mb-4 border-y dark:border-y 5xl:dark:border-primary-700',
                            summaryIsOpen || summaryIsLoading
                              ? ''
                              : 'md:border-b border-b-primary-200 5xl:border-b 5xl:border-accent-400 dark:md:border-b dark:md:border-b-accent-950 dark:5xl:border-b',
                          )
                        : '',
                      summaryIsLoading ? 'items-center' : 'items-start',
                      summary !== '' ? ' flex-shrink-0' : '',
                    )}
                  >
                    <div
                      className={twMerge(
                        'flex flex-shrink items-center w-full max-w-screen-3xl',
                        summaryIsLoading || summary !== '' ? 'py-3' : '',
                      )}
                    >
                      {summaryIsLoading ? (
                        <div className="flex flex-col gap-y-4 px-4 5xl:px-8 h-full w-full">
                          <SearchSteps steps={steps ?? []} />
                          <div className="flex flex-col gap-y-2">
                            <div className="rounded-full h-3 w-full bg-primary-300/50 dark:bg-primary-700/50 animate-pulse" />
                            <div className="rounded-full h-3 w-full bg-primary-300/50 dark:bg-primary-700/50 animate-pulse" />
                            <div className="rounded-full h-3 w-full bg-primary-300/50 dark:bg-primary-700/50 animate-pulse" />
                          </div>
                        </div>
                      ) : summary !== '' ? (
                        <>
                          <div
                            ref={markdownRef}
                            className="hidden lg:flex flex-col relative items-center w-full"
                          >
                            <div className="flex items-start w-full pl-4">
                              <div className="flex items-center gap-x-1 text-primary-900 dark:text-primary-100">
                                <HiOutlineBolt className="h-5 w-5 stroke-[1.5px]" />
                                <h3 className="text-xl font-semibold">
                                  Sweetspot AI Recommendations
                                </h3>
                              </div>
                            </div>
                            <Markdown
                              // @ts-ignore
                              components={{
                                // disable hover when streaming to prevent flashing
                                a: (props) => (
                                  // @ts-expect-error
                                  <Link
                                    {...props}
                                    target="_blank"
                                    className={twMerge(
                                      isStreaming
                                        ? 'hover:opacity-100 cursor-wait'
                                        : '',
                                      'text-blue-700 dark:text-blue-300 ',
                                    )}
                                  />
                                ),
                                pre: Pre,
                                code: Code,
                                table: Table,
                                thead: Thead,
                                th: Th,
                                td: Td,
                              }}
                              remarkPlugins={[remarkGfm]}
                              className={twMerge(
                                'pt-[4px] px-4 5xl:px-5 max-w-screen-3xl text-sm w-full',
                                PROSE_CLASSNAMES,
                                isClamped && !summaryIsOpen
                                  ? 'line-clamp-10'
                                  : '',
                              )}
                            >
                              {summary}
                            </Markdown>
                            {isClamped && (
                              <IconButton
                                onClick={handleOpenSummary}
                                className="z-10 -mb-2 hover:bg-primary-200 dark:hover:bg-primary-700"
                              >
                                {summaryIsOpen ? (
                                  <HiChevronUp className="stroke-1 h-4 w-4 text-primary-600 dark:text-primary-300" />
                                ) : (
                                  <HiChevronDown className="stroke-1 h-4 w-4 text-primary-600 dark:text-primary-300" />
                                )}
                              </IconButton>
                            )}
                            {isClamped && !summaryIsOpen && (
                              <div className="absolute -bottom-3 left-0 right-0 h-20 bg-gradient-to-b from-transparent to-white dark:to-accent-800/10 5xl:rounded-b-md" />
                            )}
                            {isStreaming && (
                              <div className="absolute top-[6px] md:top-[6px] right-[18px]">
                                <Spinner className="w-4 h-4" />
                              </div>
                            )}
                          </div>
                          <div className="lg:hidden flex w-full items-center justify-center">
                            <span className="text-sm font-medium">
                              Please use a desktop to view generative summary.
                            </span>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            }
            if (dataIsLoading) {
              return (
                <div
                  data-index={virtualizedItem.index}
                  key={virtualizedItem.index}
                  ref={virtualizer.measureElement}
                  className="flex flex-col items-center justify-center w-full px-4 5xl:px-0"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualizedItem.start}px)`,
                  }}
                >
                  {/* 366px is the height of an opportunity component (on average) */}
                  <div className="w-full max-w-screen-3xl h-[366px] mb-4 rounded-md bg-primary-200/80 american:bg-primary-100/80 dark:bg-primary-800/50 animate-pulse" />
                </div>
              )
            }
            // All non-summary rows come from displayItems
            const displayIndex = virtualizedItem.index - 1
            const di = displayItems[displayIndex]
            if (di === undefined) return null
            return (
              <div
                key={virtualizedItem.key}
                className="flex flex-col items-center justify-center w-full pb-4 px-4 5xl:px-0"
                data-index={virtualizedItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transform: `translateY(${virtualizedItem.start}px)`,
                }}
              >
                {di.kind === 'banner' ? (
                  <SledSourceRequestBanner />
                ) : (
                  <Opportunity
                    opportunity={di.opportunity}
                    canInteract={canAccessBasic ?? false}
                    requiredTagNames={requiredTagNames}
                    className="max-w-screen-3xl"
                  />
                )}
                {di.kind !== 'banner' &&
                  virtualizedItem.index === displayItems.length &&
                  data?.has_more && (
                    <div
                      ref={infiniteScrollTriggerRef}
                      className="flex items-center justify-center w-full pt-6 pb-2"
                    >
                      <Spinner />
                    </div>
                  )}
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

export default memo(SearchResponseDisplay)
